import React, { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import './CountWidget.css';
import useStore from '../../store/useStore';

const CountWidget = memo(({ title, value, unit = '', trend = null, config = {} }) => {
  const animationsEnabled = useStore((state) => state.animationsEnabled);
  const isFirstRender = useRef(true);

  // Memoize trend color calculation
  const getTrendColor = useCallback((trendValue) => {
    if (trendValue === null) return null;

    const invertColors = config.invertTrendColors || false;
    const isPositive = trendValue >= 0;

    // Normal: up=green, down=red
    // Inverted: up=red, down=green (useful for backlogs, errors, etc.)
    if (invertColors) {
      return isPositive ? '#ff6b6b' : '#51cf66';
    } else {
      return isPositive ? '#51cf66' : '#ff6b6b';
    }
  }, [config.invertTrendColors]);

  // Memoize format function
  const formatValue = useCallback((val) => {
    if (val === undefined || val === null) return '--';
    return val.toLocaleString();
  }, []);

  const currentFormatted = useMemo(() => formatValue(value), [value, formatValue]);
  const [displayValue, setDisplayValue] = useState(currentFormatted);
  const [animationKey, setAnimationKey] = useState(0);

  // Memoize content length check
  const isLongContent = useMemo(() => {
    const combinedLength = currentFormatted.length + (unit ? unit.length : 0);
    return combinedLength > 8;
  }, [currentFormatted, unit]);

  // Update display value with animation trigger
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setDisplayValue(currentFormatted);
      return;
    }

    if (currentFormatted !== displayValue) {
      if (animationsEnabled) {
        // Trigger animation by changing key
        setAnimationKey(prev => prev + 1);
        setDisplayValue(currentFormatted);
      } else {
        setDisplayValue(currentFormatted);
      }
    }
  }, [currentFormatted, animationsEnabled]);

  return (
    <div className="widget">
      <div className="widget-header">
        <span className="widget-title">{title}</span>
      </div>
      <div className="widget-body" style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <div className={`count-value-container ${isLongContent ? 'long-content' : ''}`}>
          <span
            key={animationKey}
            className={`count-value ${animationKey > 0 && animationsEnabled ? 'slide-in' : ''}`}
          >
            {displayValue}
          </span>
          {unit && (
            <span className="count-unit">
              {unit}
            </span>
          )}
        </div>
        {trend !== null && (
          <div style={{
            fontSize: '1rem',
            color: getTrendColor(trend),
            fontWeight: '600',
            transition: animationsEnabled ? 'color 0.3s ease' : 'none'
          }}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </div>
        )}
      </div>
    </div>
  );
});

CountWidget.displayName = 'CountWidget';

export default CountWidget;
